<template>
  <div
    class="text-input"
    :class="{
      'text-input--invalid': errorMessage
    }"
  >
    <label class="label t-form-label" :for="id">{{ label }}</label>
    <component
      :is="is"
      :id="id"
      class="input t-form-text"
      :type="type"
      :name="name"
      :placeholder="placeholder"
      :value="value"
      @input="handleChange"
      @blur="handleBlur"
    />
    <FormError :error-message="errorMessage" />
  </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate'

const props = withDefaults(
  defineProps<{
    name: string
    label: string
    placeholder?: string
    type?: string
    is?: 'input' | 'textarea'
  }>(),
  { is: 'input', type: 'text', placeholder: '' }
)

const id = useId()
const { handleChange, value, handleBlur, errorMessage } = useField(
  () => props.name
)
</script>

<style scoped lang="scss">
.text-input {
  display: flex;
  flex-direction: column;
  gap: rem(4px);
  width: 100%;
  margin-bottom: rem(18px);
}

input.input {
  padding: rem(13px) rem(22px);

  &:-internal-autofill-selected {
    background-color: transparent;
    -webkit-text-fill-color: $global-grey-01;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    background-color: transparent;
    -webkit-text-fill-color: $global-grey-01;
  }
}

textarea.input {
  min-height: rem(156px);
  padding: rem(22px);
  resize: none;
}

.input {
  background-color: $global-white;
  border: 1px solid $global-grey-04;
  border-radius: 22px;

  transition:
    color $transition,
    border-color $transition;

  &:disabled {
    color: $global-grey-04;
    border-color: $global-grey-05;
  }

  .text-input--invalid & {
    color: $global-error;
    border-color: $global-error;
  }

  &:not(:disabled, .text-input--invalid &) {
    &:hover {
      color: $global-grey-01;
      border-color: $global-grey-02;
    }

    &:focus {
      color: $global-grey-01;
      border-color: $global-black;
    }
  }
}

.label {
  .text-input--invalid & {
    color: $global-error;
  }
}
</style>
